// src/web/PerceptualDataAnnotation.jsx

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';
import AVIcon from '../../components/icons/AllVoicedIcons';
import PerceptualDataAnnotationFAQ from '../../components/faq/PerceptualDataAnnotationFAQ';

export default function PerceptualDataAnnotation() {
  return (
    <main className="service-landing-page perceptual-data-annotation-page">
      <Helmet>
        <title>Perceptual Data Annotation | All-Voiced</title>
        <meta
          name="description"
          content="Generate structured human-perceptual data for voice datasets through defined annotation protocols, evaluator workflows, multirater judgments, quality monitoring, and governed outputs."
        />
      </Helmet>

      <PageTracker pageName="PerceptualDataAnnotation" />

      <section className="service-landing-hero">
        <span className="services-eyebrow">
          Perceptual Data Annotation
        </span>

        <h1>
          Generate Structured Human-Perceptual Data for Voice Datasets
        </h1>

        <p>
          All-Voiced helps researchers, dataset owners, clinical
          collaborators, and voice technology teams design and coordinate
          perceptual annotation workflows that turn human listening
          judgments into structured, interpretable, and reusable data.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to="/meet"
          >
            Discuss a dataset
          </Link>

          <Link
            className="web-button web-button--secondary"
            to="/perceptual-research-infrastructure"
          >
            Explore research infrastructure
          </Link>
        </div>

        <div className="service-landing-panel">
          <span>Annotation Protocols</span>
          <span className="service-landing-separator">•</span>
          <span>Evaluator Workflows</span>
          <span className="service-landing-separator">•</span>
          <span>Multirater Annotation</span>
          <span className="service-landing-separator">•</span>
          <span>Structured Outputs</span>
        </div>
      </section>

      <section className="service-landing-section service-landing-intro">
        <span className="services-label">
          Why perceptual data annotation?
        </span>

        <h2>
          Voice datasets often lack a structured human-perceptual layer
        </h2>

        <p>
          Voice datasets may contain recordings, acoustic measures, clinical
          variables, metadata, signal-derived features, or model predictions,
          while still lacking structured information about how relevant human
          listeners perceive the voices.
        </p>

        <p>
          Depending on the project, this may include voice-quality
          attributes, severity, similarity, naturalness, intelligibility,
          vocal effort, perceived change, category membership, or other
          defined perceptual constructs.
        </p>

        <p>
          When methodologically appropriate, this layer can support
          interpretation, benchmarking, multimodal analysis, construct
          validation, research reproducibility, and human-centered technology
          evaluation.
        </p>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Annotation method
        </span>

        <h2>
          Perceptual annotation is more than attaching labels to audio
        </h2>

        <p>
          Robust perceptual data generation requires explicit decisions about
          what is being judged, how the task is structured, who evaluates,
          how multiple judgments are collected, and how outputs are
          represented and documented.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <AVIcon icon="target" size={28} color="#209385" />

            <h3>Defined perceptual constructs</h3>

            <p>
              Define what evaluators are being asked to judge and how the
              construct will be represented in the data.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="document" size={28} color="#209385" />

            <h3>Structured evaluation protocols</h3>

            <p>
              Specify task format, instructions, scales, presentation
              conditions, repetitions, and other protocol requirements.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="team" size={28} color="#209385" />

            <h3>Appropriate evaluator populations</h3>

            <p>
              Define the evaluator profile required by the project,
              including relevant expertise, background, language, or
              professional context.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="repeat" size={28} color="#209385" />

            <h3>Multirater workflows</h3>

            <p>
              Collect multiple judgments per sample where the project
              requires agreement, variability, consensus, or uncertainty
              analysis.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="dashboard" size={28} color="#209385" />

            <h3>Reusable data outputs</h3>

            <p>
              Produce structured perceptual data and documentation suitable
              for research, benchmarking, evaluation, or downstream analysis.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Supported workflows
        </span>

        <h2>
          What Perceptual Data Annotation can support
        </h2>

        <p>
          The exact workflow depends on the dataset, perceptual construct,
          evaluator workflow, governance requirements, consent conditions,
          and intended downstream use.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <h3>Annotation protocol design</h3>

            <p>
              Define the perceptual construct, evaluation task, rating scale,
              instructions, repetition strategy, and output structure.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Evaluator workflow configuration</h3>

            <p>
              Coordinate evaluator groups, phases, access, sample allocation,
              and repeated evaluations according to the project design.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Multirater annotation</h3>

            <p>
              Collect multiple independent or coordinated judgments per
              sample where the project requires agreement, consensus, or
              variability analysis.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Quality monitoring</h3>

            <p>
              Monitor completion, repeated items, response patterns,
              disagreement, variability, and reliability-related indicators
              where supported by the configured workflow.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Agreement and uncertainty representation</h3>

            <p>
              Preserve or summarize agreement, distributions, variability,
              disagreement, consensus, or uncertainty according to the
              project objective.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Structured data and documentation</h3>

            <p>
              Deliver agreed response data, sample identifiers, evaluator
              metadata where appropriate, annotation summaries, data
              dictionaries, reports, dashboards, or exports according to
              project scope.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section service-landing-highlight">
        <span className="services-label">
          Annotation workflow architecture
        </span>

        <h2>
          From voice dataset to structured perceptual layer
        </h2>

        <p>
          All-Voiced connects the dataset, perceptual construct, annotation
          protocol, evaluator workflow, and output structure within one
          coordinated process.
        </p>

        <div className="service-landing-panel">
          <span>Voice dataset</span>
          <span className="service-landing-separator">↓</span>
          <span>Perceptual construct definition</span>
          <span className="service-landing-separator">↓</span>
          <span>Annotation protocol</span>
          <span className="service-landing-separator">↓</span>
          <span>Evaluator workflow</span>
          <span className="service-landing-separator">↓</span>
          <span>Sample allocation and presentation</span>
          <span className="service-landing-separator">↓</span>
          <span>Multirater judgments</span>
          <span className="service-landing-separator">↓</span>
          <span>Quality and variability review</span>
          <span className="service-landing-separator">↓</span>
          <span>Structured perceptual dataset</span>
        </div>
      </section>

      <section className="service-landing-section service-landing-callout">
        <span className="services-label">
          Project contexts
        </span>

        <h2>
          Structured perceptual data across research and technology contexts
        </h2>

        <p>
          Perceptual Data Annotation can support projects that need
          interpretable human-perceptual data for research, benchmarking,
          technology evaluation, or downstream analysis.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <h3>Research dataset enrichment</h3>

            <p>
              Add structured perceptual variables to existing audio
              collections for secondary analysis, publication, or future
              reuse.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Voice technology evaluation</h3>

            <p>
              Generate human reference judgments that may support
              benchmarking, interpretation, error analysis, or comparison
              with model-derived outputs.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Vocal biomarker research</h3>

            <p>
              Add a human-perceptual layer that may help relate
              signal-derived features or biomarker candidates to clinically
              and perceptually meaningful constructs.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Clinical dataset annotation</h3>

            <p>
              Coordinate structured perceptual evaluation of clinical voice
              samples using an agreed protocol and evaluator workflow.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Benchmark and reference-set development</h3>

            <p>
              Support curated reference sets with repeated judgments,
              documented protocols, and project-defined quality criteria.
            </p>
          </div>

          <div className="service-landing-card">
            <h3>Cross-cultural and multilingual annotation</h3>

            <p>
              Coordinate evaluator populations across languages or contexts
              when the construct and protocol require culturally or
              linguistically informed judgments.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Evaluator systems and uncertainty
        </span>

        <h2>
          The quality of the dataset depends on how evaluators and variation
          are represented
        </h2>

        <p>
          Perceptual annotation quality depends on who evaluates, what
          instructions they receive, how samples are allocated, how many
          judgments are collected, and how repeated ratings, disagreement,
          and incomplete participation are handled.
        </p>

        <p>
          Evaluator background, expertise, language, professional context,
          familiarization, training, or calibration may also matter,
          depending on the construct and intended use. Evaluator expertise
          should therefore be treated as a project resource rather than as a
          generic labor pool.
        </p>

        <p>
          Perceptual judgments may vary because of sample difficulty,
          construct ambiguity, evaluator interpretation, expertise, language
          or cultural context, scale use, or genuine multidimensionality.
          This variation can reveal information about the samples, the
          construct, the evaluators, or the annotation protocol.
        </p>

        <div className="service-landing-statement">
          <p>
            Disagreement is data, not merely noise.
          </p>

          <p>
            Some projects need consensus labels; others benefit from
            preserving individual judgments, distributions, subgroup
            patterns, disagreement, and evaluator-level variability.
          </p>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Data and deliverables
        </span>

        <h2>
          From individual judgments to reusable perceptual data
        </h2>

        <p>
          Depending on project scope, deliverables may include raw evaluator
          responses, sample identifiers, project-specific evaluator
          identifiers, repeated-item responses, structured ratings or
          categories, aggregate summaries, agreement and variability
          outputs, completion records, annotation protocols, and data
          dictionaries.
        </p>

        <p>
          Dashboards, reports, structured exports, evaluator background
          metadata, consensus outputs, and reliability-related indicators may
          also be included where appropriate, legally permissible, and
          explicitly defined in the project scope.
        </p>
      </section>

      <section className="service-landing-section service-landing-callout">
        <span className="services-label">
          Data governance
        </span>

        <h2>
          Perceptual data must remain aligned with governance and consent
        </h2>

        <p>
          Annotation projects should establish who owns or stewards the
          dataset, whether the audio may be used for the proposed purpose,
          how consent and privacy conditions apply, who may access the data,
          how long it may be retained, and under what conditions annotations
          may be shared, published, reused, or used downstream.
        </p>

        <p>
          Research, commercial, and AI-related uses may require different
          permissions and governance conditions.
        </p>

        <p>
          All-Voiced can support a governed annotation workflow, but the
          dataset owner or responsible institution must have the necessary
          rights, permissions, and ethical basis for the proposed use.
        </p>
      </section>

      <section className="service-landing-section service-landing-callout">
        <span className="services-label">
          How we work
        </span>

        <h2>
          From focused pilots to coordinated annotation workflows
        </h2>

        <p>
          Projects may begin with a pilot subset, one perceptual construct,
          one evaluator group, or a limited reference set. They may later
          extend to larger datasets, multiple constructs, repeated phases,
          multilingual annotation, multiple evaluator panels, benchmark-set
          development, or human-model comparison.
        </p>

        <p>
          The appropriate starting point should be methodologically
          meaningful, operationally feasible, and proportionate to the
          intended use of the data.
        </p>

        <div className="services-steps">
          <div className="services-step">
            <span className="services-step-number">1</span>

            <div>
              <h3>Understand the dataset and intended use</h3>

              <p>
                Clarify dataset composition, ownership, sample
                characteristics, research, clinical, or technology
                objective, target constructs, timeline, and governance
                constraints.
              </p>
            </div>
          </div>

          <div className="services-step">
            <span className="services-step-number">2</span>

            <div>
              <h3>Define the annotation protocol</h3>

              <p>
                Specify evaluator requirements, task format, scales,
                instructions, sample allocation, repetitions, quality
                monitoring, and output structure.
              </p>
            </div>
          </div>

          <div className="services-step">
            <span className="services-step-number">3</span>

            <div>
              <h3>Configure and pilot</h3>

              <p>
                Configure the workflow, test representative samples, review
                the evaluator experience, verify data capture and workflow
                behavior, and refine the protocol before full annotation.
              </p>
            </div>
          </div>

          <div className="services-step">
            <span className="services-step-number">4</span>

            <div>
              <h3>Run, monitor, and deliver</h3>

              <p>
                Support or coordinate the agreed annotation workflow, monitor
                completion and defined quality indicators, and deliver the
                agreed structured outputs.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="service-landing-section service-landing-resources">
        <span className="services-label">
          Related service line
        </span>

        <h2>
          Data annotation and research workflows are connected
        </h2>

        <p>
          Perceptual Data Annotation starts from a dataset and the need to
          generate a structured human-perceptual layer. Perceptual Research
          Infrastructure starts from a research protocol and a project-specific
          perceptual workflow. Some projects require both.
        </p>

        <div className="service-landing-resource-card">
          <div>
            <h3>Perceptual Research Infrastructure</h3>

            <p>
              Explore the study-centered service line for clinical and
              research projects requiring a configured perceptual workflow.
            </p>
          </div>

          <Link
            className="web-button web-button--secondary"
            to="/perceptual-research-infrastructure"
          >
            Explore research infrastructure
          </Link>
        </div>
      </section>

      <PerceptualDataAnnotationFAQ />

      <section className="services-final-cta">
        <h2>
          Add a structured human-perceptual layer to your voice dataset
        </h2>

        <p>
          Discuss your dataset, intended use, perceptual constructs,
          evaluator needs, and required outputs to define an appropriate
          annotation workflow.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to="/meet"
          >
            Discuss a dataset
          </Link>

          <Link
            className="web-button web-button--secondary"
            to="/perceptual-research-infrastructure"
          >
            Explore research infrastructure
          </Link>
        </div>
      </section>

      <ScrollTracker pageName="PerceptualDataAnnotation" />
    </main>
  );
}